import { useLazyQuery } from "@apollo/client";
import { ensureFileExtension } from "encamp-shared/src/utils/ensureFileExtension";
import { saveAs } from "file-saver";
import { gql } from "generated-graphql";
import { Document } from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import { useAlerts } from "../components/Alerts/AlertProvider";

const GET_DOCUMENT_WITH_DOWNLOAD_LINK = gql(`
  query GetDocument($id: ID!) {
    document(id: $id) {
      id
      title
      fileExtension
      storageLink
      downloadLink
    }
  }
`);

const downloadFile = async (url: string, filename: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download document: ${response.statusText}`);
  }
  const download = await response.blob();

  saveAs(download, filename);
};

const getExtensionFromString = (candidate: string): string | null => {
  const parts = candidate.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : null;
};

const getFileNameForDownload = ({
  title,
  fileExtension,
  storageLink,
}: Pick<Document, "title" | "fileExtension" | "storageLink">) => {
  // Prefer the document's real extension over one parsed from the title: a
  // title like "Invoice v1.2" has no real extension, so keying off the title
  // would download it without one. Fall back to the extension embedded in the
  // S3 key. ensureFileExtension leaves the name unchanged when it already ends
  // in the extension.
  const extension = fileExtension || getExtensionFromString(storageLink);
  return extension ? ensureFileExtension(title, extension) : title;
};

export const useDocumentDownload = () => {
  const [downloadLink, setDownloadLink] = useState<string | undefined>();
  const [getDocument] = useLazyQuery(GET_DOCUMENT_WITH_DOWNLOAD_LINK, {
    fetchPolicy: "no-cache", // This ensures the download link is always fetched
  });
  const alerts = useAlerts();

  const fetchAndHandleDownload = useCallback(
    async ({ documentId }: { documentId: string }) => {
      const { data, error } = await getDocument({
        variables: { id: documentId },
      });

      if (!data?.document || !data.document.downloadLink || error) {
        alerts.error("An error occurred while viewing the record");
        return;
      }
      setDownloadLink(data.document.downloadLink);
      const filename = getFileNameForDownload(data.document);

      // Await the download so callers can track when the file actually
      // finishes saving (not just when the metadata query resolves), and so a
      // failed fetch surfaces an alert instead of being silently swallowed.
      try {
        await downloadFile(data.document.downloadLink, filename);
      } catch (e) {
        alerts.error(
          "An error occurred while downloading the document",
          e instanceof Error ? e : undefined
        );
      }
    },
    [getDocument, alerts]
  );

  const clearDownloadLink = () => {
    setDownloadLink(undefined);
  };

  return {
    downloadLink,
    fetchAndHandleDownload,
    clearDownloadLink,
  };
};
